'use client';

import { loginWithOAuth } from '@adornis/authentication/oauth-client/client/loginWithOAuth.js';
import { allowGuestLeave } from '../../lib/guest-user-id.js';
import { logger } from '@adornis/base/logging.js';
import { currentUser, subscribeCurrentUser } from '@adornis/users/client/currentUser.js';
import { login } from '@adornis/users/client/loginFunctions.js';
import { ArrowLeft, CheckCircle2, GraduationCap, Mail, Presentation, X } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next'; // <-- Import für Übersetzung
import { classifyAuthError } from '../../lib/auth-errors.js';
import {
  EMAIL_CODE_EXPIRED,
  EMAIL_CODE_INVALID,
  EMAIL_NOT_VERIFIED,
  resendVerificationEmailMutation,
  verifyEmailCodeMutation,
} from '../../../db/api/email-verification.js';
import { requestPasswordResetMutation } from '../../../db/api/password-reset.js';
import { signUpMutation } from '../../../db/api/sign-up.js';
import { type Subject, ALL_SUBJECTS } from '../../../db/subjects.js';
import GoogleIconPath from '../../assets/google.svg';
import { Button } from '../../components/ui/button.js';
import LabeledInput, { MultiSelectDropdown, validateInput } from '../../components/ui/labeled-inputs.js';

interface AuthModalProps {
  onContinueAsGuest?: () => void;
  initialMode?: 'login' | 'signup';
  onAuthSuccess?: () => void;
  /**
   * Fixes the role, e.g. 'student' when opened from the `/join` class-code landing page, and skips the role step.
   * Without it sign-up first asks for the role, which also decides a new Google account's profile. Signing in never
   * asks: the account's stored role applies.
   */
  defaultRole?: 'teacher' | 'student';
  /** A Google sign-in used an address without an account (client/lib/oauth-no-account.ts); shown as a notice. */
  noAccount?: boolean;
}

export function AuthModal({
  onContinueAsGuest,
  initialMode = 'login',
  onAuthSuccess,
  defaultRole,
  noAccount,
}: AuthModalProps) {
  const { t, i18n } = useTranslation();

  logger.info(`🔥 AUTH MODAL WURDE GERENDERT!`);
  const [isLogin, setIsLogin] = useState(initialMode === 'login');
  const [isRoleChosen, setIsRoleChosen] = useState(defaultRole !== undefined);
  const [showValidation, setShowValidation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authError, setAuthError] = useState<string | null>(() =>
    noAccount
      ? t('AuthModal.googleNoAccount', {
          defaultValue: 'Für diese Google-Adresse gibt es noch kein Konto. Bitte registriere dich zuerst.',
        })
      : null,
  );

  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistSuccess] = useState(false);

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Set after sign-up or after a login refused for an unconfirmed address; shows the code screen. The password goes
  // along with the code: the server takes the code only together with it (email-verification.ts).
  const [pendingVerification, setPendingVerification] = useState<{
    email: string;
    password: string;
    fromSignUp: boolean;
  } | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');

  const [formData, setFormData] = useState({
    role: defaultRole ?? 'teacher',
    firstName: '',
    lastName: '',
    school: '',
    subjects: [] as Subject[],
    email: '',
    password: '',
    confirmPassword: '',
  });

  const requiredFields = {
    firstName: true,
    lastName: true,
    school: true,
    subjects: true,
    email: true,
    password: true,
    confirmPassword: true,
  };

  const handleSignUpChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubjectToggle = (subject: Subject) => {
    setFormData(prev => {
      const nextSubjects = prev.subjects.includes(subject)
        ? prev.subjects.filter(s => s !== subject)
        : [...prev.subjects, subject];
      return { ...prev, subjects: nextSubjects };
    });
  };

  const hasValidationErrors = () => {
    return (
      (requiredFields.firstName && !validateInput(formData.firstName, 'text')) ||
      (requiredFields.lastName && !validateInput(formData.lastName, 'text')) ||
      (requiredFields.school && !validateInput(formData.school, 'text')) ||
      // Students aren't asked to pick subjects at signup — they add them freely later in their
      // profile, since they aren't restricted to a fixed subject list the way teachers are.
      (formData.role === 'teacher' && requiredFields.subjects && !validateInput(formData.subjects, 'multiselect')) ||
      (requiredFields.email && !validateInput(formData.email, 'email')) ||
      (requiredFields.password && !validateInput(formData.password, 'password')) ||
      (requiredFields.confirmPassword && !validateInput(formData.confirmPassword, 'password'))
    );
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setAuthError(null);
    setShowValidation(false);
    setShowForgotPassword(false);
  };

  // The server signed this session in; wait until the client sees the user, then close.
  // Die Gast-Konversation übernimmt page.tsx beim Nutzerwechsel (claimGuestConversationAfterLogin).
  const completeSignIn = async () => {
    await subscribeCurrentUser();

    await new Promise<void>(resolve => {
      const sub = currentUser().subscribe(u => {
        if ((u as any)?._id || (u as any)?.id) {
          sub.unsubscribe();
          resolve();
        }
      });
    });

    onAuthSuccess?.();
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setIsSubmitting(true);

    try {
      await login(loginEmail, loginPassword);
      await completeSignIn();
    } catch (err) {
      if (String((err as any)?.message ?? err).includes(EMAIL_NOT_VERIFIED)) {
        setPendingVerification({ email: loginEmail.trim(), password: loginPassword, fromSignUp: false });
        setVerificationCode('');
        setResendState('idle');
        return;
      }
      setAuthError(t('AuthModal.loginError', { defaultValue: 'E-Mail oder Passwort ist falsch.' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignUpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowValidation(true);
    setAuthError(null);

    if (hasValidationErrors()) return;

    if (formData.password !== formData.confirmPassword) {
      setAuthError(t('AuthModal.passwordMismatch', { defaultValue: 'Die Passwörter stimmen nicht überein.' }));
      return;
    }

    setIsSubmitting(true);

    try {
      const normalizedSubjects = Array.isArray(formData.subjects)
        ? formData.subjects.filter(subject => typeof subject === 'string' && subject.trim().length > 0)
        : [];

      // Creates account and profile and sends the code mail. No session is opened: the user signs in by entering the
      // code here, in this tab, so the guest state stays as it is until then.
      await signUpMutation(
        formData.role,
        formData.firstName,
        formData.lastName,
        formData.school,
        normalizedSubjects,
        formData.email,
        formData.password,
        i18n.language || 'de',
      )();

      setResendState('idle');
      setVerificationCode('');
      setPendingVerification({ email: formData.email.trim(), password: formData.password, fromSignUp: true });
    } catch (error: any) {
      // The server's own message is English and may carry internal names, so only known cases are translated.
      const kind = classifyAuthError(error);
      setAuthError(
        kind === 'password'
          ? t('AuthModal.passwordInvalid', { defaultValue: 'Das Passwort erfüllt die Anforderungen nicht.' })
          : kind === 'email'
            ? t('AuthModal.emailInvalid', { defaultValue: 'Bitte gib eine gültige E-Mail-Adresse ein.' })
            : t('AuthModal.signupFailed', { defaultValue: 'Registrierung fehlgeschlagen. Bitte versuche es erneut.' }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingVerification || verificationCode.length !== 6) return;

    setIsSubmitting(true);
    setAuthError(null);
    try {
      await verifyEmailCodeMutation(pendingVerification.email, pendingVerification.password, verificationCode)();
      await completeSignIn();
    } catch (err) {
      const message = String((err as any)?.message ?? err);
      setAuthError(
        message.includes(EMAIL_CODE_EXPIRED)
          ? t('AuthModal.verifyCodeExpired', {
              defaultValue: 'Der Code ist abgelaufen oder wurde zu oft falsch eingegeben. Fordere einen neuen Code an.',
            })
          : message.includes(EMAIL_CODE_INVALID)
            ? t('AuthModal.verifyCodeInvalid', { defaultValue: 'Der Code ist falsch.' })
            : t('AuthModal.forgotPasswordProblem', {
                defaultValue: 'Es gab ein Problem. Bitte versuche es später erneut.',
              }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // The password is required: the server only sends a code to the account it belongs to (email-verification.ts).
  const handleResendVerification = async (email: string, password: string) => {
    if (!email || resendState === 'sending') return;
    setResendState('sending');
    try {
      await resendVerificationEmailMutation(email.trim(), password)();
      setResendState('sent');
      setVerificationCode('');
      setAuthError(null);
    } catch (error) {
      logger.error(error, 'Resending verification email failed');
      setResendState('idle');
      setAuthError(
        t('AuthModal.forgotPasswordProblem', { defaultValue: 'Es gab ein Problem. Bitte versuche es später erneut.' }),
      );
    }
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotPasswordEmail) return;

    setIsSubmitting(true);
    setAuthError(null);
    try {
      await requestPasswordResetMutation(forgotPasswordEmail.trim(), false)();
      setForgotPasswordSuccess(true);
    } catch (error) {
      setAuthError(
        t('AuthModal.forgotPasswordProblem', { defaultValue: 'Es gab ein Problem. Bitte versuche es später erneut.' }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = () => {
    // Die Gast-Konversation übernimmt page.tsx nach dem Redirect; hier geht nur die Rolle für ein neues Profil mit.
    // Beim Anmelden gibt es keine Rollenwahl (das Konto behält seine Rolle), nur bei der Registrierung oder wenn die
    // Rolle feststeht (/join).
    const successRedirect = new URL(window.location.href);
    successRedirect.searchParams.delete('guestConversationId');
    if (!isLogin || defaultRole !== undefined) successRedirect.searchParams.set('oauthRole', formData.role);

    // Leaving for Google is intended; the guest's work is claimed after the redirect (app/page.tsx).
    allowGuestLeave();
    loginWithOAuth({ successRedirect: successRedirect.toString() });
  };

  const localizedSubjects = ALL_SUBJECTS;
  // The role only matters for a new account, so only sign-up asks for it.
  const showRoleStep = !isLogin && !isRoleChosen;

  if (showWaitlist) {
    return (
      <div className="w-full max-w-md bg-white dark:bg-card rounded-2xl shadow-2xl p-8 border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in-95 duration-300">
        {waitlistSuccess ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
              {t('AuthModal.waitlistTitle')}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mb-8 leading-relaxed">{t('AuthModal.waitlistDesc')}</p>
            <Button onClick={() => setShowWaitlist(false)} className="w-full bg-brand-500 text-white">
              {t('AuthModal.backToLogin')}
            </Button>
          </div>
        ) : (
          <div className="text-center">{t('AuthModal.waitlistInactive')}</div>
        )}
      </div>
    );
  }

  if (pendingVerification) {
    return (
      <div className="w-full max-w-md bg-white dark:bg-card rounded-2xl shadow-2xl p-8 border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in-95 duration-300">
        <div className="text-center py-4">
          <div className="w-16 h-16 bg-brand-surface text-brand-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="h-8 w-8" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
            {t('AuthModal.verifyEmailTitle', { defaultValue: 'Bestätige deine E-Mail-Adresse' })}
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mb-2 leading-relaxed">
            {pendingVerification.fromSignUp
              ? t('AuthModal.verifyEmailDesc', { defaultValue: 'Wir haben dir einen Bestätigungscode geschickt an:' })
              : t('AuthModal.emailNotVerified', {
                  defaultValue:
                    'Bitte bestätige zuerst deine E-Mail-Adresse mit dem Code, den wir dir geschickt haben an:',
                })}
          </p>
          <p className="font-semibold text-slate-800 dark:text-slate-100 mb-1 break-all">{pendingVerification.email}</p>
          {pendingVerification.fromSignUp && (
            <button
              type="button"
              onClick={() => {
                // Back to the filled-in form; a corrected address starts a new registration, the mistyped one expires.
                setPendingVerification(null);
                setAuthError(null);
                setIsLogin(false);
              }}
              className="text-sm text-brand-500 hover:underline mb-6"
            >
              {t('AuthModal.verifyEmailWrongAddress', { defaultValue: 'Falsche Adresse? Ändern' })}
            </button>
          )}
          <form onSubmit={handleVerifyCode} className="space-y-4 mt-6 text-left">
            <div>
              <label
                htmlFor="verification-code"
                className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
              >
                {t('AuthModal.verifyCodeLabel', { defaultValue: 'Bestätigungscode' })}
              </label>
              <input
                id="verification-code"
                name="verificationCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                value={verificationCode}
                // Pasted codes may come with spaces ("123 456"); only the digits count.
                onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                className="w-full px-4 py-3 border rounded-lg bg-white dark:bg-secondary text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:border-transparent border-slate-200 dark:border-slate-700 focus:ring-brand-500 text-center text-2xl font-semibold tracking-[0.5em]"
              />
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
              {t('AuthModal.verifyEmailHint', {
                defaultValue: 'Der Code ist 15 Minuten gültig. Schau auch im Spam-Ordner nach.',
              })}
            </p>
            {authError && <p className="text-sm text-red-500">{authError}</p>}
            <Button
              type="submit"
              disabled={isSubmitting || verificationCode.length !== 6}
              className="w-full bg-brand-500 text-white py-6"
            >
              {t('AuthModal.verifyCodeButton', { defaultValue: 'Bestätigen' })}
            </Button>
          </form>
          {resendState === 'sent' ? (
            <p className="text-sm text-emerald-700 mt-4">
              {t('AuthModal.verifyEmailResent', { defaultValue: 'Wir haben dir einen neuen Code geschickt.' })}
            </p>
          ) : (
            <button
              type="button"
              onClick={() => handleResendVerification(pendingVerification.email, pendingVerification.password)}
              disabled={resendState === 'sending'}
              className="text-sm text-brand-500 hover:underline mt-4 disabled:opacity-50"
            >
              {t('AuthModal.verifyEmailResend', { defaultValue: 'Neuen Code senden' })}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setLoginEmail(pendingVerification.email);
              setPendingVerification(null);
              setAuthError(null);
              setIsLogin(true);
            }}
            className="block mx-auto mt-4 text-sm text-slate-500 dark:text-slate-400 hover:text-brand-500 transition-colors"
          >
            {t('AuthModal.backToLogin')}
          </button>
        </div>
      </div>
    );
  }

  if (showForgotPassword) {
    return (
      <div className="w-full max-w-md bg-white dark:bg-card rounded-2xl shadow-2xl p-8 border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in-95 duration-300">
        <button
          onClick={() => setShowForgotPassword(false)}
          className="flex items-center text-sm text-slate-500 dark:text-slate-400 mb-6 hover:text-brand-500 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> {t('AuthModal.backToLogin')}
        </button>

        {forgotPasswordSuccess ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
              {t('AuthModal.linkSentTitle')}
            </h2>
            {/* The same text whether or not the address has an account: the server answers the same in every case. */}
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">{t('AuthModal.linkSentBody')}</p>
            <Button onClick={() => setShowForgotPassword(false)} className="w-full bg-brand-500 text-white py-6 mt-6">
              {t('AuthModal.linkSentDesc')}
            </Button>
            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(false);
                setForgotPasswordSuccess(false);
                setAuthError(null);
                setIsLogin(false);
              }}
              className="mt-4 text-sm text-brand-500 hover:underline"
            >
              {t('AuthModal.linkSentRegister')}
            </button>
          </div>
        ) : (
          <div>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-6">
              {t('AuthModal.forgotPasswordTitle')}
            </h2>
            <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
              <LabeledInput
                id="forgot-email"
                name="forgotPasswordEmail"
                label={t('AuthModal.emailLabel')}
                type="email"
                value={forgotPasswordEmail}
                onChange={e => setForgotPasswordEmail(e.target.value)}
                required
              />
              {authError && <p className="text-sm text-red-500">{authError}</p>}
              <Button
                type="submit"
                disabled={isSubmitting || !forgotPasswordEmail}
                className="w-full bg-brand-500 text-white py-6"
              >
                {t('AuthModal.requestLink')}
              </Button>
            </form>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full max-w-md bg-white dark:bg-card rounded-2xl shadow-2xl p-8 border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto custom-scrollbar">
      <div className="relative text-center mb-6">
        {!isLogin && isRoleChosen && defaultRole === undefined && (
          <button
            type="button"
            onClick={() => {
              setIsRoleChosen(false);
              setAuthError(null);
            }}
            aria-label={t('AuthModal.changeRole')}
            title={t('AuthModal.changeRole')}
            className="absolute -left-2 -top-4 p-1 text-slate-400 dark:text-slate-500 hover:text-brand-500 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}
        <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
          {isLogin ? t('AuthModal.welcomeBack') : t('AuthModal.createAccount')}
        </h2>
        {showRoleStep && (
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{t('AuthModal.roleQuestion')}</p>
        )}
        {onContinueAsGuest && (
          <button
            type="button"
            onClick={onContinueAsGuest}
            aria-label={t('AuthModal.continueGuest')}
            className="absolute -right-2 -top-4 p-1 text-slate-300 dark:text-slate-600 hover:text-slate-500 dark:hover:text-slate-400 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {showRoleStep ? (
        <div>
          <div className="grid grid-cols-2 gap-3">
            {(['teacher', 'student'] as const).map(role => {
              const Icon = role === 'teacher' ? Presentation : GraduationCap;
              return (
                <button
                  key={role}
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, role }));
                    setIsRoleChosen(true);
                    setAuthError(null);
                  }}
                  className="flex flex-col items-center gap-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-card px-4 py-6 text-base font-semibold text-slate-700 dark:text-slate-200 hover:border-brand-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  <Icon className="h-8 w-8 text-brand-500" />
                  {t(role === 'teacher' ? 'AuthModal.roleTeacher' : 'AuthModal.roleStudent')}
                </button>
              );
            })}
          </div>
          {authError && <p className="mt-4 text-sm text-red-500 text-center">{authError}</p>}
        </div>
      ) : isLogin ? (
        <form onSubmit={handleLoginSubmit} className="space-y-4">
          <LabeledInput
            id="login-email"
            name="loginEmail"
            label={t('AuthModal.emailLabel')}
            type="email"
            value={loginEmail}
            onChange={e => setLoginEmail(e.target.value)}
            required
          />
          <div>
            <LabeledInput
              id="login-password"
              name="loginPassword"
              label={t('AuthModal.passwordLabel')}
              type="password"
              value={loginPassword}
              onChange={e => setLoginPassword(e.target.value)}
              required
            />
            <div className="flex justify-end mt-1.5">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-brand-500 hover:underline"
              >
                {t('AuthModal.forgotPasswordLink')}
              </button>
            </div>
          </div>
          {authError && <p className="text-sm text-red-500">{authError}</p>}
          <Button type="submit" disabled={isSubmitting} className="w-full bg-brand-500 text-white py-6">
            {t('AuthModal.loginButton')}
          </Button>

          <div className="relative mt-0 mb-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-xs text-slate-400">{t('AuthModal.or')}</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="flex w-full items-center justify-center gap-3 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <img src={GoogleIconPath} alt="Google" className="h-5 w-5" />
            {t('AuthModal.continueWithGoogle')}
          </button>
        </form>
      ) : (
        <form onSubmit={handleSignUpSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <LabeledInput
              id="firstName"
              name="firstName"
              label={t('AuthModal.firstNameLabel')}
              value={formData.firstName}
              onChange={handleSignUpChange}
              required
              showValidation={showValidation}
            />
            <LabeledInput
              id="lastName"
              name="lastName"
              label={t('AuthModal.lastNameLabel')}
              value={formData.lastName}
              onChange={handleSignUpChange}
              required
              showValidation={showValidation}
            />
          </div>
          <LabeledInput
            id="school"
            name="school"
            label={t('AuthModal.schoolLabel')}
            value={formData.school}
            onChange={handleSignUpChange}
            required
            showValidation={showValidation}
          />
          {formData.role === 'teacher' && (
            <MultiSelectDropdown
              id="subjects"
              label={t('AuthModal.subjectsLabel')}
              options={localizedSubjects}
              selectedValues={formData.subjects}
              onToggle={handleSubjectToggle}
              required
              showValidation={showValidation}
              formatOption={subject => t(`Subjects.${subject}`)}
            />
          )}
          <LabeledInput
            id="email"
            name="email"
            label={t('AuthModal.emailLabel')}
            type="email"
            value={formData.email}
            onChange={handleSignUpChange}
            required
            showValidation={showValidation}
          />
          <LabeledInput
            id="password"
            name="password"
            label={t('AuthModal.passwordLabel')}
            type="password"
            value={formData.password}
            onChange={handleSignUpChange}
            required
            showValidation={showValidation}
            hint={t('AuthModal.passwordRequirements', {
              defaultValue: 'Mindestens 8 Zeichen, mit Groß- und Kleinbuchstaben, einer Ziffer und einem Sonderzeichen.',
            })}
          />
          <LabeledInput
            id="confirmPassword"
            name="confirmPassword"
            label={t('AuthModal.confirmPasswordLabel')}
            type="password"
            value={formData.confirmPassword}
            onChange={handleSignUpChange}
            required
            showValidation={showValidation}
          />
          {authError && <p className="text-sm text-red-500">{authError}</p>}
          <Button type="submit" disabled={isSubmitting} className="w-full bg-brand-500 text-white py-6">
            {t('AuthModal.signupButton')}
          </Button>

          <div className="relative mt-0 mb-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-xs text-slate-400">{t('AuthModal.or')}</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="flex w-full items-center justify-center gap-3 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <img src={GoogleIconPath} alt="Google" className="h-5 w-5" />
            {t('AuthModal.continueWithGoogle')}
          </button>
        </form>
      )}

      <div className="mt-6 text-center text-sm text-slate-600 dark:text-slate-400">
        <button type="button" onClick={toggleMode} className="text-brand-500 font-semibold hover:underline">
          {isLogin ? t('AuthModal.registerHere') : t('AuthModal.loginHere')}
        </button>
      </div>

      {onContinueAsGuest && (
        <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-700 text-center">
          <button
            type="button"
            onClick={onContinueAsGuest}
            className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
          >
            {t('AuthModal.continueGuest')}
          </button>
        </div>
      )}
    </div>
  );
}
